import { useEffect, useMemo, useRef, useState } from 'react';
import { useApiErrors, useEvent, useLazyCommand } from '@soap/modules';
import { Checkbox } from '../ui/checkbox';
import { ThinkingAnimation } from './ThinkingAnimation';
import { ExternalLessonInputSection, formatManualLessonText, parseManualLessonText } from './CreateUnitForm/ExternalLessonInputSection';
import {
  type CreateUnitRequest,
  type ManualLessonEntry,
  type UnitContentSource,
  unitContentSources
} from './CreateUnitForm/createUnitTypes';
import { useUnitCreationProgress } from './LessonsPage/hooks/useUnitCreationProgress';
import { studentCurriculumYearNodeId } from './LessonsPage/utils/curriculumUtils';
import type { CreateUnitTopicChoice } from './LessonsPage/utils/topicUtils';
import { maxTopicsPerUnit, messages } from './LessonsPage/constants';
import { YouTubeApiServicesNotice } from './YouTubeApiServicesNotice';
import { useLearningStylesCatalog } from '../hooks/useLearningStylesCatalog';

type UnitMode = 'SingleSubject' | 'Blended';
type Step = 'mode' | 'firstTopic' | 'secondTopic' | 'application' | 'source' | 'details' | 'external' | 'externalPreview' | 'materials';

type TopicChoice = CreateUnitTopicChoice;

type MaterialsPrefs = NonNullable<CreateUnitRequest['materialsPrefs']>;

const readingAudiences = {
  parentBrief: 'ParentBrief',
  studentReading: 'StudentReading'
} as const;

const readingAudienceOptions = [
  readingAudiences.parentBrief,
  readingAudiences.studentReading
] as const;

const readingDepths = {
  minimal: 'Minimal',
  moderate: 'Moderate',
  comprehensive: 'Comprehensive'
} as const;

const readingDepthOptions = [
  readingDepths.minimal,
  readingDepths.moderate,
  readingDepths.comprehensive
] as const;

type ReadingAudienceValue = (typeof readingAudiences)[keyof typeof readingAudiences];
type ReadingDepthValue = (typeof readingDepths)[keyof typeof readingDepths];

const defaultMaterials: MaterialsPrefs = {
  useWorksheets: true,
  readingAudience: readingAudiences.studentReading,
  readingDepth: readingDepths.moderate,
  includeVideos: true,
  minimalWordsMin: 80,
  minimalWordsMax: 180,
  moderateWordsMin: 200,
  moderateWordsMax: 400,
  comprehensiveWordsMin: 400,
  comprehensiveWordsMax: 800
};

function formatWordRangeLabel(min?: number, max?: number): string | null {
  if (min == null || max == null) return null;
  if (min <= 0 && max <= 0) return null;
  return `~${min}–${max} words`;
}

function readingAudienceLabel(option: ReadingAudienceValue): string {
  return option === readingAudiences.parentBrief ? 'Parent Brief' : 'Student Reading';
}

function readingDepthLabel(option: ReadingDepthValue, prefs: MaterialsPrefs): string {
  if (option === readingDepths.minimal) {
    const range = formatWordRangeLabel(prefs.minimalWordsMin, prefs.minimalWordsMax);
    return range ? `Minimal (${range})` : 'Minimal';
  }
  if (option === readingDepths.moderate) {
    const range = formatWordRangeLabel(prefs.moderateWordsMin, prefs.moderateWordsMax);
    return range ? `Moderate (${range})` : 'Moderate';
  }
  const range = formatWordRangeLabel(prefs.comprehensiveWordsMin, prefs.comprehensiveWordsMax);
  return range ? `Comprehensive (${range})` : 'Comprehensive';
}

export interface CreateUnitWizardProps {
  open: boolean;
  onClose: () => void;
  studentId: string | null | undefined;
  studentCurriculumData: any;
  onCreateUnit: (request: CreateUnitRequest) => void;
  onLoadYearTopics: (nodeId: string) => void;
  loadedNodeIds: Set<string>;
  loadingNodeIds: Set<string>;
  /** When set with initialTopics, wizard opens past topic picking for that mode. */
  initialUnitCreationMode?: UnitMode | null;
  initialTopics?: CreateUnitTopicChoice[];
}

function leafTopics(topics: any[], context: Omit<TopicChoice, 'id' | 'name' | 'parentGroupId'>, parentGroupId?: string): TopicChoice[] {
  return topics.flatMap((topic) => {
    const children = topic.e180_ChildTopics ?? [];
    const topicId = String(topic.e180_TopicId ?? '').trim();
    const groupId = parentGroupId ?? (children.length > 0 ? topicId : undefined);
    if (children.length > 0) return leafTopics(children, context, groupId);
    return topicId
      ? [{ ...context, id: topicId, name: topic.e180_Name || 'Untitled topic', parentGroupId }]
      : [];
  });
}

function learningStyleValue(style: any): string {
  return String(style?.Value ?? style?.value ?? style?.Key ?? style?.key ?? '').trim();
}

function blobListKey(ids: string[]): string {
  return ids.join('|');
}

function lessonsFromExtractEvent(event: any): ManualLessonEntry[] {
  const raw = (event?.e440_Lessons ?? event?.E440_Lessons ?? []) as any[];
  return raw
    .map(row => ({
      title: String(row?.e440_Title ?? row?.E440_Title ?? '').trim(),
      summary: String(row?.e440_Summary ?? row?.E440_Summary ?? '').trim()
    }))
    .filter(entry => entry.title);
}

/** Short examples shown under each style option (same copy as the previous Create Unit dialog). */
function learningStyleExample(value: string): string | null {
  switch (value) {
    case 'Conceptual':
      return 'Theory';
    case 'Field Trip':
      return 'Real-world experiences';
    case 'Kinesthetic':
      return 'Hands-on activities';
    default:
      return null;
  }
}

export function CreateUnitWizard({
  open,
  onClose,
  studentId,
  studentCurriculumData,
  onCreateUnit,
  onLoadYearTopics,
  loadedNodeIds,
  loadingNodeIds,
  initialUnitCreationMode = null,
  initialTopics = []
}: CreateUnitWizardProps) {
  const learningStyles = useLearningStylesCatalog(open);
  const [step, setStep] = useState<Step>('mode');
  const [mode, setMode] = useState<UnitMode>('SingleSubject');
  const [topics, setTopics] = useState<TopicChoice[]>([]);
  const [subjectId, setSubjectId] = useState('');
  const [keyStageId, setKeyStageId] = useState('');
  const [yearId, setYearId] = useState('');
  const [includeApplicationLesson, setIncludeApplicationLesson] = useState(true);
  const [styles, setStyles] = useState<string[]>([]);
  const [contentSource, setContentSource] = useState<UnitContentSource>(unitContentSources.aiGenerated);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [externalProviderId, setExternalProviderId] = useState('');
  const [manualLessonText, setManualLessonText] = useState('');
  const [sourceBlobIds, setSourceBlobIds] = useState<string[]>([]);
  const [materialsPrefs, setMaterialsPrefs] = useState<MaterialsPrefs>(defaultMaterials);
  const [studentReadingLocked, setStudentReadingLocked] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractedBlobKey, setExtractedBlobKey] = useState('');
  const [externalBusy, setExternalBusy] = useState(false);
  const [sendSuggestDefaults] = useLazyCommand('C325v1_SuggestUnitSupportingMaterialDefaults');
  const [sendExtractLessons] = useLazyCommand('C441v1_ExtractLessonsFromDocument');
  const isAiCreate = contentSource === unitContentSources.aiGenerated;
  const isAiCreateRef = useRef(isAiCreate);
  isAiCreateRef.current = isAiCreate;
  const isExtractingRef = useRef(isExtracting);
  isExtractingRef.current = isExtracting;
  const sourceBlobIdsRef = useRef(sourceBlobIds);
  sourceBlobIdsRef.current = sourceBlobIds;
  const creationProgress = useUnitCreationProgress(isCreating && isAiCreate);
  const runFinishingStepRef = useRef(creationProgress?.runFinishingStep);
  runFinishingStepRef.current = creationProgress?.runFinishingStep;

  useEffect(() => {
    if (!open) {
      setStep('mode');
      setMode('SingleSubject');
      setTopics([]);
      setSubjectId('');
      setKeyStageId('');
      setYearId('');
      setIncludeApplicationLesson(true);
      setStyles([]);
      setContentSource(unitContentSources.aiGenerated);
      setSpecialInstructions('');
      setExternalProviderId('');
      setManualLessonText('');
      setSourceBlobIds([]);
      setMaterialsPrefs(defaultMaterials);
      setStudentReadingLocked(false);
      setAttempted(false);
      setError(null);
      setIsSuggesting(false);
      setIsExtracting(false);
      setIsCreating(false);
      setExtractedBlobKey('');
      setExternalBusy(false);
      return;
    }

    const seededMode =
      initialUnitCreationMode === 'SingleSubject' || initialUnitCreationMode === 'Blended'
        ? initialUnitCreationMode
        : null;
    const seededTopics = initialTopics?.length ? initialTopics : [];
    if (seededMode && seededTopics.length > 0) {
      setMode(seededMode);
      setTopics(seededTopics);
      setIncludeApplicationLesson(seededMode === 'SingleSubject');
      setStep(seededMode === 'Blended' ? 'source' : 'application');
      const first = seededTopics[0];
      setSubjectId(first?.subjectId ?? '');
      setKeyStageId(first?.keyStageId ?? '');
      setYearId(first?.yearId ?? '');
      setStyles([]);
      setContentSource(unitContentSources.aiGenerated);
      setSpecialInstructions('');
      setExternalProviderId('');
      setManualLessonText('');
      setSourceBlobIds([]);
      setMaterialsPrefs(defaultMaterials);
      setStudentReadingLocked(false);
      setAttempted(false);
      setError(null);
      setIsSuggesting(false);
      setIsExtracting(false);
      setIsCreating(false);
      setExtractedBlobKey('');
      setExternalBusy(false);
      return;
    }

    setStep('mode');
    setMode('SingleSubject');
    setTopics([]);
    setSubjectId('');
    setKeyStageId('');
    setYearId('');
    setIncludeApplicationLesson(true);
  }, [open]); // Seed from initial* only when the dialog opens

  useApiErrors(() => {
    setIsSuggesting(false);
    setIsExtracting(false);
    setIsCreating(false);
  });
  useEvent({
    eventName: 'E250v1_UnitCreated',
    onEventReceived: async () => {
      if (isAiCreateRef.current && runFinishingStepRef.current) {
        await runFinishingStepRef.current();
      }
      setIsCreating(false);
      onClose();
    }
  });
  useEvent({
    eventName: 'E317v1_GotUnitSupportingMaterialDefaults',
    onEventReceived: (event: any) => {
      if (!isSuggesting) return;
      const locked = !!(event?.e317_StudentReadingLocked ?? event?.E317_StudentReadingLocked);
      setStudentReadingLocked(locked);
      setMaterialsPrefs({
        useWorksheets: locked
          ? false
          : (event?.e317_UseWorksheetsSuggested ?? event?.E317_UseWorksheetsSuggested ?? true),
        readingAudience: locked
          ? readingAudiences.parentBrief
          : (event?.e317_ReadingAudience ?? event?.E317_ReadingAudience ?? readingAudiences.studentReading),
        readingDepth: event?.e317_ReadingDepth ?? event?.E317_ReadingDepth ?? readingDepths.moderate,
        includeVideos: event?.e317_IncludeVideosSuggested ?? event?.E317_IncludeVideosSuggested ?? true,
        minimalWordsMin: event?.e317_MinimalWordsMin ?? event?.E317_MinimalWordsMin ?? event?.e317_LightWordsMin ?? event?.E317_LightWordsMin ?? 80,
        minimalWordsMax: event?.e317_MinimalWordsMax ?? event?.E317_MinimalWordsMax ?? event?.e317_LightWordsMax ?? event?.E317_LightWordsMax ?? 180,
        moderateWordsMin: event?.e317_ModerateWordsMin ?? event?.E317_ModerateWordsMin ?? 200,
        moderateWordsMax: event?.e317_ModerateWordsMax ?? event?.E317_ModerateWordsMax ?? 400,
        comprehensiveWordsMin: event?.e317_ComprehensiveWordsMin ?? event?.E317_ComprehensiveWordsMin ?? 400,
        comprehensiveWordsMax: event?.e317_ComprehensiveWordsMax ?? event?.E317_ComprehensiveWordsMax ?? 800
      });
      setIsSuggesting(false);
      setStep('materials');
    }
  });
  useEvent({
    eventName: 'E440v1_GotExtractedLessonsFromDocument',
    onEventReceived: (event: any) => {
      if (!isExtractingRef.current) return;
      const formatted = formatManualLessonText(lessonsFromExtractEvent(event));
      setManualLessonText(formatted);
      setExtractedBlobKey(blobListKey(sourceBlobIdsRef.current));
      setIsExtracting(false);
      setAttempted(false);
      setStep('externalPreview');
    }
  });

  const subjects = studentCurriculumData?.e180_Subjects ?? [];
  const subject = subjects.find((candidate: any) => String(candidate.e180_SubjectId) === subjectId);
  const keyStages = subject?.e180_KeyStages ?? [];
  const keyStage = keyStages.find((candidate: any) => String(candidate.e180_KeyStageId) === keyStageId);
  const years = keyStage?.e180_Years ?? [];
  const year = years.find((candidate: any) => String(candidate.e180_YearId) === yearId);
  const yearNodeId = useMemo(() => {
    if (!subject || !keyStage || !year) return null;
    const keyStageIndex = subject.e180_KeyStages.indexOf(keyStage);
    const yearIndex = keyStage.e180_Years.indexOf(year);
    if (keyStageIndex < 0 || yearIndex < 0) return null;
    return studentCurriculumYearNodeId(subject, keyStageIndex, yearIndex);
  }, [subject, keyStage, year]);
  const yearTopicsReady =
    yearNodeId != null &&
    (loadedNodeIds.has(yearNodeId) || (year?.e180_Topics?.length ?? 0) > 0);
  const yearTopicsLoading =
    yearNodeId != null && !yearTopicsReady && loadingNodeIds.has(yearNodeId);

  useEffect(() => {
    if (!open || !yearNodeId || yearTopicsReady || yearTopicsLoading) return;
    onLoadYearTopics(yearNodeId);
  }, [open, yearNodeId, yearTopicsReady, yearTopicsLoading, onLoadYearTopics]);

  const choices = useMemo(() => {
    if (!subject || !keyStage || !year) return [];
    return leafTopics(year.e180_Topics ?? [], {
      subjectId: String(subject.e180_SubjectId),
      subjectName: subject.e180_Name ?? 'Subject',
      keyStageId: String(keyStage.e180_KeyStageId),
      keyStageName: keyStage.e180_Name ?? 'Key stage',
      yearId: String(year.e180_YearId),
      yearName: year.e180_YearLabel ?? 'Year'
    });
  }, [subject, keyStage, year]);
  const currentTopicStep = step === 'firstTopic' ? 1 : 2;
  // Keep the selections in order: all single-topic selections, then second blended topic.
  const firstTopics = mode === 'Blended' ? topics.slice(0, 1) : topics;
  const secondTopics = mode === 'Blended' ? topics.slice(1, 2) : [];
  const activeTopics = currentTopicStep === 1 ? firstTopics : secondTopics;
  const selectedIds = new Set(activeTopics.map(t => t.id));

  if (!open) return null;

  const resetBranch = () => {
    setKeyStageId('');
    setYearId('');
  };
  const selectYear = (value: string) => {
    setYearId(value);
    const selectedYear = years.find((candidate: any) => String(candidate.e180_YearId) === value);
    if (!subject || !keyStage || !selectedYear) return;
    const keyStageIndex = subject.e180_KeyStages.indexOf(keyStage);
    const yearIndex = keyStage.e180_Years.indexOf(selectedYear);
    if (keyStageIndex < 0 || yearIndex < 0) return;
    const nodeId = studentCurriculumYearNodeId(subject, keyStageIndex, yearIndex);
    const alreadyReady =
      loadedNodeIds.has(nodeId) || (selectedYear.e180_Topics?.length ?? 0) > 0;
    if (!alreadyReady && !loadingNodeIds.has(nodeId)) {
      onLoadYearTopics(nodeId);
    }
  };
  const toggleTopic = (choice: TopicChoice) => {
    setError(null);
    if (mode === 'Blended') {
      if (currentTopicStep === 2 && firstTopics[0]?.subjectId === choice.subjectId) {
        setError('Choose the second topic from a different subject.');
        return;
      }
      const replacement = currentTopicStep === 1 ? [choice, ...secondTopics] : [...firstTopics, choice];
      setTopics(selectedIds.has(choice.id) ? replacement.filter(t => t.id !== choice.id) : replacement);
      return;
    }
    if (selectedIds.has(choice.id)) {
      setTopics(topics.filter(t => t.id !== choice.id));
      return;
    }
    if (topics.length >= maxTopicsPerUnit) {
      setError(messages.topicLimitMessage);
      return;
    }
    if (topics.length > 0) {
      const existingGroup = topics[0].parentGroupId;
      const sameGroup = existingGroup && choice.parentGroupId === existingGroup;
      const bothRoot = !existingGroup && !choice.parentGroupId;
      if (!sameGroup && !bothRoot) {
        setError('Choose leaf topics from one parent topic group, or choose only root-level topics.');
        return;
      }
    }
    setTopics([...topics, choice]);
  };
  const nextFromTopics = () => {
    setAttempted(true);
    if (activeTopics.length === 0) return;
    if (mode === 'SingleSubject') {
      if (topics.length > maxTopicsPerUnit) {
        setError(messages.topicLimitMessage);
        return;
      }
      const first = topics[0];
      const sameBranch = topics.every(
        topic =>
          topic.subjectId === first.subjectId &&
          topic.keyStageId === first.keyStageId &&
          topic.yearId === first.yearId
      );
      if (!sameBranch) {
        setError('All topics in a single-subject unit must be from the same subject, key stage, and year.');
        return;
      }
    }
    if (mode === 'Blended' && currentTopicStep === 2 && firstTopics[0]?.subjectId === secondTopics[0]?.subjectId) {
      setError('Choose the second topic from a different subject.');
      return;
    }
    if (mode === 'Blended' && currentTopicStep === 1) {
      setStep('secondTopic');
      setSubjectId('');
      resetBranch();
      setAttempted(false);
      return;
    }
    setStep(mode === 'SingleSubject' ? 'application' : 'source');
    setAttempted(false);
  };
  const nextFromApplication = () => {
    if (includeApplicationLesson && styles.length === 0) {
      setAttempted(true);
      return;
    }
    setAttempted(false);
    setStep('source');
  };
  const requestMaterials = () => {
    if (!studentId) {
      setError('A student must be selected.');
      return;
    }
    setIsSuggesting(true);
    sendSuggestDefaults({ c325_StudentId: studentId, c325_TopicIds: topics.map(t => t.id) });
  };
  const nextFromDetails = () => {
    requestMaterials();
  };
  const handleSourceBlobIdsChange = (ids: string[]) => {
    setSourceBlobIds(ids);
    if (blobListKey(ids) !== extractedBlobKey) {
      setExtractedBlobKey('');
      setManualLessonText('');
    }
  };
  const beginExtract = (ids: string[]) => {
    setAttempted(false);
    setError(null);
    setIsExtracting(true);
    sendExtractLessons({ c441_SourceBlobIds: ids });
  };
  const nextFromExternal = () => {
    if (!externalProviderId) {
      setAttempted(true);
      return;
    }
    if (sourceBlobIds.length === 0) {
      setAttempted(false);
      setError(null);
      setStep('externalPreview');
      return;
    }
    if (blobListKey(sourceBlobIds) === extractedBlobKey) {
      setStep('externalPreview');
      return;
    }
    beginExtract(sourceBlobIds);
  };
  const create = () => {
    if (!studentId) return;
    const external = contentSource !== unitContentSources.aiGenerated;
    if (external && (!externalProviderId || parseManualLessonText(manualLessonText).length === 0)) {
      setAttempted(true);
      return;
    }
    setIsCreating(true);
    onCreateUnit({
      topicIds: topics.map(t => t.id),
      unitCreationMode: mode,
      includeApplicationLesson: mode === 'SingleSubject' ? includeApplicationLesson : false,
      contentSource,
      studentId,
      learningStyles: mode === 'SingleSubject' && includeApplicationLesson ? styles : [],
      specialInstructions,
      materialsPrefs: external ? undefined : materialsPrefs,
      externalProviderId: external ? externalProviderId : undefined,
      manualLessonEntries: external ? parseManualLessonText(manualLessonText) : undefined,
      sourceBlobIds: external ? sourceBlobIds : undefined
    });
  };
  const back = () => {
    const prior: Record<Step, Step | undefined> = {
      mode: undefined,
      firstTopic: 'mode',
      secondTopic: 'firstTopic',
      application: 'firstTopic',
      source: mode === 'SingleSubject' ? 'application' : 'secondTopic',
      details: 'source',
      external: 'source',
      externalPreview: 'external',
      materials: 'details'
    };
    const target = prior[step];
    if (target) setStep(target);
  };
  const extractStatusMessage =
    contentSource === unitContentSources.externalBook
      ? 'Analyzing your image...'
      : 'Reading your document...';
  const isTopicPicker = step === 'firstTopic' || step === 'secondTopic';
  const topicTitle = step === 'secondTopic' ? 'Choose your second topic' : 'Choose topic(s)';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b p-5">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Create Unit</h2>
            <p className="text-sm text-gray-500">
              {isExtracting
                ? extractStatusMessage
                : isSuggesting
                  ? 'Suggesting materials defaults…'
                  : 'Build a unit from your student’s curriculum.'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">✕</button>
        </div>
        <div className="overflow-y-auto p-5">
          {isSuggesting || isExtracting || isCreating ? (
            <ThinkingAnimation
              message={
                isCreating
                  ? 'Creating Your Unique Unit...'
                  : isExtracting
                    ? extractStatusMessage
                    : 'Suggesting materials defaults...'
              }
              progressSteps={isCreating ? creationProgress?.stages : undefined}
              currentProgressStepIndex={isCreating ? (creationProgress?.stageIndex ?? 0) : 0}
            />
          ) : (
            <>
              {step === 'mode' && <div className="space-y-3">
                <h3 className="text-lg font-semibold">What type of unit?</h3>
                <button type="button" onClick={() => setMode('SingleSubject')} className={`w-full rounded-lg border-2 p-4 text-left ${mode === 'SingleSubject' ? 'border-action-blue bg-action-blue/10' : 'border-gray-200'}`}>
                  <strong>Single subject unit</strong><p className="mt-1 text-sm text-gray-600">Choose one subject branch and one or more related topics.</p>
                </button>
                <button type="button" onClick={() => setMode('Blended')} className={`w-full rounded-lg border-2 p-4 text-left ${mode === 'Blended' ? 'border-action-blue bg-action-blue/10' : 'border-gray-200'}`}>
                  <strong>Blended unit</strong><p className="mt-1 text-sm text-gray-600">Choose one topic from each of two different subjects.</p>
                </button>
              </div>}
              {isTopicPicker && <div className="space-y-4">
                <div><h3 className="text-lg font-semibold">{topicTitle}</h3><p className="text-sm text-gray-600">{mode === 'Blended' ? 'Choose exactly one leaf topic.' : 'Choose leaf topics from one parent group, or root-level topics only.'}</p></div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <select value={subjectId} onChange={e => { setSubjectId(e.target.value); resetBranch(); }} className="rounded border p-2"><option value="">Subject</option>{subjects.map((s: any) => <option key={s.e180_SubjectId} value={s.e180_SubjectId}>{s.e180_Name}</option>)}</select>
                  <select value={keyStageId} disabled={!subjectId} onChange={e => { setKeyStageId(e.target.value); setYearId(''); }} className="rounded border p-2"><option value="">Key Stage</option>{keyStages.map((ks: any) => <option key={ks.e180_KeyStageId} value={ks.e180_KeyStageId}>{ks.e180_Name}</option>)}</select>
                  <select value={yearId} disabled={!keyStageId} onChange={e => selectYear(e.target.value)} className="rounded border p-2"><option value="">Year</option>{years.map((y: any) => <option key={y.e180_YearId} value={y.e180_YearId}>{y.e180_YearLabel}</option>)}</select>
                </div>
                {yearId && yearNodeId && !yearTopicsReady && (
                  <p className="text-sm text-gray-600">Loading topics…</p>
                )}
                {yearId && yearTopicsReady && (
                  <div className="max-h-64 space-y-1 overflow-y-auto rounded border p-2">
                  {choices.map(choice => <label key={choice.id} className="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-gray-50">
                    <Checkbox checked={selectedIds.has(choice.id)} onCheckedChange={() => toggleTopic(choice)} />
                    <span>{choice.name}</span>
                  </label>)}
                  {choices.length === 0 && <p className="p-2 text-sm text-gray-500">There are no leaf topics in this year.</p>}
                </div>
                )}
                {error && <p className="text-sm text-destructive-action">{error}</p>}
                {attempted && activeTopics.length === 0 && <p className="text-sm text-destructive-action">Choose {mode === 'Blended' ? 'one topic' : 'at least one topic'}.</p>}
              </div>}
              {step === 'application' && <div className="space-y-4">
                <h3 className="text-lg font-semibold">Application lesson</h3>
                <label className="flex items-center gap-3">
                  <Checkbox checked={includeApplicationLesson} onCheckedChange={v => setIncludeApplicationLesson(v === true)} />
                  Include a real-world application lesson
                </label>
                <p className="text-sm text-gray-600">
                  This extra lesson applies the selected topics to a real-world scenario for the student. Topic lessons stay traditional.
                </p>
                {includeApplicationLesson && (
                  <div>
                    <h3 className="text-lg font-semibold">Learning styles</h3>
                    <p className="mt-1 text-sm text-gray-600">These styles shape the application lesson only.</p>
                    <div className="mt-2 grid gap-2 sm:grid-cols-3">
                      {learningStyles.map((style, i) => {
                        const value = learningStyleValue(style);
                        const example = learningStyleExample(value);
                        return (
                          <button
                            type="button"
                            key={value || i}
                            onClick={() =>
                              setStyles(previous =>
                                previous.includes(value) ? previous.filter(x => x !== value) : [...previous, value]
                              )
                            }
                            className={`rounded border-2 p-3 text-left ${
                              styles.includes(value)
                                ? 'border-action-blue bg-action-blue/10 text-action-blue-dark'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            <div className="text-sm font-medium">{value}</div>
                            {example && <div className="mt-1 text-xs text-gray-500">{example}</div>}
                          </button>
                        );
                      })}
                    </div>
                    {attempted && styles.length === 0 && (
                      <p className="mt-2 text-sm text-destructive-action">Choose at least one learning style.</p>
                    )}
                  </div>
                )}
              </div>}
              {step === 'source' && <div className="space-y-3"><h3 className="text-lg font-semibold">How should lessons be created?</h3>{[
                [unitContentSources.aiGenerated, 'Generate lessons with AI'],
                [unitContentSources.externalBook, 'Use lessons from a book'],
                [unitContentSources.externalOnline, 'Use lessons from an online provider']
              ].map(([value, label]) => <button key={value} type="button" onClick={() => {
                const nextSource = value as UnitContentSource;
                if (nextSource !== contentSource) {
                  setExternalProviderId('');
                  setSourceBlobIds([]);
                  setManualLessonText('');
                  setExtractedBlobKey('');
                }
                setContentSource(nextSource);
              }} className={`w-full rounded-lg border-2 p-4 text-left ${contentSource === value ? 'border-action-blue bg-action-blue/10' : 'border-gray-200'}`}>{label}</button>)}</div>}
              {step === 'details' && <div className="space-y-5"><div><h3 className="text-lg font-semibold">Special instructions</h3><textarea value={specialInstructions} onChange={e => setSpecialInstructions(e.target.value)} rows={3} className="mt-2 w-full rounded border p-3" placeholder="Optional instructions for this unit" /></div></div>}
              {step === 'external' && (
                <ExternalLessonInputSection
                  contentSource={contentSource}
                  providerId={externalProviderId}
                  onProviderChange={setExternalProviderId}
                  sourceBlobIds={sourceBlobIds}
                  onSourceBlobIdsChange={handleSourceBlobIdsChange}
                  onBusyChange={setExternalBusy}
                  onPhotoCaptured={ids => {
                    handleSourceBlobIdsChange(ids);
                    beginExtract(ids);
                  }}
                  providerError={attempted && !externalProviderId ? 'Select a provider.' : undefined}
                />
              )}
              {step === 'externalPreview' && (
                <div className="mb-5">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Lesson preview</h3>
                  <p className="text-sm text-gray-600 mb-2">
                    These are the lessons we plan to create. Edit the list, then create the unit. One lesson per
                    line. Optionally use title | description format.
                  </p>
                  {attempted && parseManualLessonText(manualLessonText).length === 0 ? (
                    <div className="text-destructive-action text-sm mb-2 bg-destructive-action/10 border border-destructive-action/30 rounded p-2">
                      Add at least one lesson name.
                    </div>
                  ) : null}
                  <textarea
                    value={manualLessonText}
                    onChange={e => setManualLessonText(e.target.value)}
                    placeholder={'Lesson 1: Introduction\nLesson 2 | Fractions overview'}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-action-blue focus:border-action-blue resize-none"
                    rows={8}
                  />
                </div>
              )}
              {step === 'materials' && (
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold">Supporting materials</h3>

                  <div>
                    <h4 className="mb-2 text-base font-semibold text-gray-900">Worksheets</h4>
                    {studentReadingLocked ? (
                      <p className="mb-3 text-sm text-gray-600">
                        Worksheets are unavailable because this student&apos;s reading ability is None (cannot do
                        independent written work on a sheet). You can change reading ability on{' '}
                        <span className="font-medium">Profiles → Students</span>.
                      </p>
                    ) : (
                      <p className="mb-3 text-sm text-gray-600">
                        Include printable worksheets on topic and blended lessons. Real-world application lessons never include a worksheet.
                      </p>
                    )}
                    <label
                      className={`flex items-center gap-3 text-sm ${
                        studentReadingLocked ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-gray-800'
                      }`}
                    >
                      <Checkbox
                        checked={materialsPrefs.useWorksheets}
                        disabled={studentReadingLocked}
                        onCheckedChange={v => {
                          if (studentReadingLocked) return;
                          setMaterialsPrefs(p => ({ ...p, useWorksheets: v === true }));
                        }}
                      />
                      Use worksheets
                    </label>
                  </div>

                  <div>
                    <h4 className="mb-2 text-base font-semibold text-gray-900">Reading audience</h4>
                    {studentReadingLocked ? (
                      <p className="mb-3 text-sm text-gray-600">
                        Student Reading is unavailable because this student&apos;s reading ability is None (cannot read
                        independently). A Parent Brief still gives you concept material to teach from. Change reading
                        ability on <span className="font-medium">Profiles → Students</span> to unlock Student Reading.
                      </p>
                    ) : (
                      <p className="mb-3 text-sm text-gray-600">
                        Who the concept reading is written for. Doesn't affect length just tone.
                      </p>
                    )}
                    <div className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {readingAudienceOptions.map(option => {
                        const isSelected = materialsPrefs.readingAudience === option;
                        const disabled =
                          studentReadingLocked && option === readingAudiences.studentReading;
                        return (
                          <button
                            key={option}
                            type="button"
                            disabled={disabled}
                            onClick={() => {
                              if (disabled) return;
                              setMaterialsPrefs(p => ({ ...p, readingAudience: option }));
                            }}
                            className={`rounded-lg border-2 p-3 text-left transition-colors ${
                              isSelected
                                ? 'border-action-blue bg-action-blue/10 text-action-blue-dark'
                                : disabled
                                  ? 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-400'
                                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            <div className="text-sm font-medium">{readingAudienceLabel(option)}</div>
                          </button>
                        );
                      })}
                    </div>

                    <h4 className="mb-2 text-base font-semibold text-gray-900">Reading depth</h4>
                    <p className="mb-3 text-sm text-gray-600">
                      How long the concept reading is. Each lesson can include a textbook-style excerpt (rules,
                      explanations, examples). Word ranges are suggested for this student and unit.
                    </p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      {readingDepthOptions.map(option => {
                        const isSelected = materialsPrefs.readingDepth === option;
                        return (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setMaterialsPrefs(p => ({ ...p, readingDepth: option }))}
                            className={`rounded-lg border-2 p-3 text-left transition-colors ${
                              isSelected
                                ? 'border-action-blue bg-action-blue/10 text-action-blue-dark'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            <div className="text-sm font-medium">
                              {readingDepthLabel(option, materialsPrefs)}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <h4 className="mb-2 text-base font-semibold text-gray-900">Videos</h4>
                    <label className="flex cursor-pointer items-start gap-3">
                      <Checkbox
                        checked={materialsPrefs.includeVideos}
                        onCheckedChange={v =>
                          setMaterialsPrefs(p => ({ ...p, includeVideos: v === true }))
                        }
                        className="mt-0.5"
                      />
                      <span className="text-sm text-gray-700">
                        Include suggested videos in supporting materials
                      </span>
                    </label>
                    {materialsPrefs.includeVideos ? (
                      <div className="pl-6">
                        <YouTubeApiServicesNotice />
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {!isSuggesting && !isExtracting && !isCreating && <div className="flex justify-between border-t p-5"><div>{step !== 'mode' && <button onClick={back} className="rounded bg-gray-100 px-4 py-2 text-gray-700">Back</button>}</div><div className="flex gap-3"><button onClick={onClose} className="rounded bg-gray-100 px-4 py-2 text-gray-700">Cancel</button>{step === 'mode' && <button onClick={() => setStep('firstTopic')} className="rounded bg-action-blue px-5 py-2 text-white">Next</button>}{isTopicPicker && <button onClick={nextFromTopics} className="rounded bg-action-blue px-5 py-2 text-white">Next</button>}{step === 'application' && <button onClick={nextFromApplication} className="rounded bg-action-blue px-5 py-2 text-white">Next</button>}{step === 'source' && <button onClick={() => setStep(contentSource === unitContentSources.aiGenerated ? 'details' : 'external')} className="rounded bg-action-blue px-5 py-2 text-white">Next</button>}{step === 'details' && <button onClick={nextFromDetails} className="rounded bg-action-blue px-5 py-2 text-white">Next</button>}{step === 'external' && <button onClick={nextFromExternal} disabled={externalBusy} className="rounded bg-action-blue px-5 py-2 text-white disabled:opacity-50">{externalBusy ? 'Uploading...' : 'Next'}</button>}{step === 'externalPreview' && <button onClick={create} className="rounded bg-action-blue px-5 py-2 text-white">Create Unit</button>}{step === 'materials' && <button onClick={create} className="rounded bg-action-blue px-5 py-2 text-white">Create Unit</button>}</div></div>}
      </div>
    </div>
  );
}
